import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import ConfirmModal from "../components/modals/ConfirmModal";
import LinkProfessionalTypeModal from "../components/modals/LinkProfessionalTypeModal";
import {
  entityProfessionalTypeService,
  ProfTypeItem,
} from "../services/entityProfessionalTypeService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import ResultCount from "../components/ResultCount";

const EntityProfessionalTypes: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName =
    params.get("entityName") || user?.entityName || "Entidade";

  const [linked, setLinked] = useState<ProfTypeItem[]>([]);
  const [search, setSearch] = useState("");
  const [available, setAvailable] = useState<ProfTypeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [toUnlink, setToUnlink] = useState<ProfTypeItem | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const data = await entityProfessionalTypeService.getByEntity(entityId);
      setLinked(data.linked);
      setAvailable(data.available);
    } catch (error) {
      console.error("Erro ao carregar tipos profissionais:", error);
      showToast("Erro ao carregar os tipos de profissionais.", "error");
    } finally {
      setLoading(false);
    }
  };

  /** Minusculas e sem acento: quem busca "fono" espera achar "Fonoaudiólogo". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const filtered = linked.filter(
    (item) => !search.trim() || normalize(item.name || "").includes(normalize(search)),
  );

  const clearFilters = () => setSearch("");
  const hasFilterChanges = search !== "";

  useEffect(() => {
    document.title = `${entityName} / Tipos de Profissionais`;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const handleUnlink = async () => {
    if (!toUnlink || unlinking) return;
    setUnlinking(true);
    try {
      await entityProfessionalTypeService.unlink(entityId, toUnlink.id);
      showToast("Tipo de profissional desvinculado.", "success");
      setToUnlink(null);
      load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao desvincular. Tente novamente.";
      showToast(message, "error");
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <div className="c4u-page-header c4u-page-header--in-panel">
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Tipos de Profissionais</h1>
              <p className="c4u-page-header__subtitle">Tipos de profissionais habilitados para esta entidade. Vincule os que atendem aqui; tipos em uso por profissionais não podem ser desvinculados.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por tipo"
                  placeholder="Buscar"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton
                  onClick={() => setIsLinkOpen(true)}
                  title="Vincular tipo de profissional"
                />
              </div>
            </div>

          <ResultCount shown={filtered.length} total={linked.length} />
          <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box
                className="c4u-table__header">
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "1 1 auto", minWidth: 200, textAlign: "left" }}
                >
                  Tipo de profissional
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 90px", justifyContent: "flex-end" }}
                >
                  Ações
                </Box>
              </Box>

              <Box className="admin-plans-table-body">
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 6,
                    }}
                  >
                    <CircularProgress sx={{ color: colors.primary }} />
                  </Box>
                ) : filtered.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: colors.textSecondary,
                    }}
                  >
                    Nenhum tipo de profissional vinculado. Use "Vincular" para
                    adicionar.
                  </Box>
                ) : (
                  filtered.map((item) => (
                    <Box
                      key={item.id}
                      className="c4u-table__row"
                    >
                      <Box
                        className="admin-plans-cell admin-plans-name"
                        sx={{
                          flex: "1 1 auto",
                          minWidth: 200,
                          textAlign: "left",
                          fontWeight: 600,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                        data-label="Tipo de profissional"
                      >
                        {item.name}
                      </Box>
                      <Box
                        className="admin-plans-cell admin-plans-actions"
                        sx={{ flex: "0 0 90px", textAlign: "right" }}
                        data-label="Ações"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Tooltip
                            title={
                              item.inUse
                                ? "Não é possível desvincular: há profissionais com este tipo"
                                : "Desvincular"
                            }
                          >
                            <span>
                              <IconButton
                                className="c4u-row-action"
                                size="small"
                                disabled={item.inUse}
                                onClick={() => setToUnlink(item)}
                                sx={{
                                  backgroundColor: "#dc3545",
                                  color: "white",
                                  "&:hover": { backgroundColor: "#c82333" },
                                  "&.Mui-disabled": {
                                    backgroundColor: "#e9a3aa",
                                    color: "white",
                                  },
                                }}
                              >
                                <Delete sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <LinkProfessionalTypeModal
        isOpen={isLinkOpen}
        onClose={() => setIsLinkOpen(false)}
        entityId={entityId}
        available={available}
        onLinked={() => {
          showToast("Tipo(s) de profissional vinculado(s).", "success");
          load();
        }}
      />

      <ConfirmModal
        isOpen={!!toUnlink}
        title="Desvincular Tipo de Profissional"
        message={`Deseja realmente desvincular "${
          toUnlink?.name || ""
        }" desta entidade?`}
        confirmButtonText="Desvincular"
        onConfirm={handleUnlink}
        onCancel={() => setToUnlink(null)}
        type="danger"
        isProcessing={unlinking}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default EntityProfessionalTypes;
